import { request } from '@/common/services/request';
import { createIdempotencyKey } from '@/common/utils/idempotency';
import type {
  DepositClaimResult,
  DepositConfig,
  DepositRecord,
  ExchangeDirection,
  ExchangeQuote,
  ExchangeRecord,
  FundsBalances,
  LedgerRecord,
  OffsetPage,
  Withdrawal,
  WithdrawalConfig,
} from '@/types/api';

export interface ExchangeSettings {
  version: string;
  feeBps: number;
  inputDecimals: number;
  limitsEnabled: boolean;
  minGrossUsdt: string;
  maxGrossUsdt: string;
}

export const getExchangeConfig = (): Promise<ExchangeSettings> =>
  request({ url: '/v1/exchanges/config', method: 'GET', showError: false });

export interface PantaExchangeEligibility {
  effectiveVip: number;
  tradingVip: number;
  financeVip: number;
  airdropVip: boolean;
  restricted: boolean;
  stakedPanta: string;
  requiredPanta: string;
  shortfallPanta: string;
  ruleVersion: number;
  allowedDirections: ExchangeDirection[];
  checkedAt: string;
}

export const getExchangeEligibility = (): Promise<PantaExchangeEligibility> =>
  request({ url: '/v1/exchanges/eligibility', method: 'GET', showError: false });

export type {
  DepositClaimResult,
  DepositConfig,
  DepositRecord,
  DepositStatus,
  ExchangeDirection,
  ExchangeQuote,
  ExchangeRecord,
  FundsBalances,
  LedgerRecord,
  Withdrawal,
  WithdrawalConfig,
  WithdrawalStatus,
} from '@/types/api';

/** GET /v1/funds/balances — USDT + WATT (do not sum) */
export const getFundsBalances = (): Promise<FundsBalances> => {
  return request({
    url: '/v1/funds/balances',
    method: 'GET',
  });
};

/** GET /v1/deposits/config */
export const getDepositsConfig = (options: {expectedWallet?:string;showError?:boolean} = {}): Promise<DepositConfig> => {
  return request({
    url: '/v1/deposits/config',
    ...options,
    method: 'GET',
  });
};

/** POST /v1/deposits/claim — 202 Accepted, not credited yet */
export const claimDeposit = (txHash: string, options: {expectedWallet?:string} = {}): Promise<DepositClaimResult> => {
  return request({
    url: '/v1/deposits/claim',
    ...options,
    method: 'POST',
    data: { txHash },
    showError: false,
  });
};

/** GET /v1/deposits — page size fixed at 20 */
export const listDeposits = (
  offset = 0
): Promise<OffsetPage<DepositRecord>> => {
  return request({
    url: '/v1/deposits',
    method: 'GET',
    data: { offset },
  });
};

/** GET /v1/deposits/:id — §20.2 */
export const getDeposit = (
  id: string
): Promise<DepositRecord & { ledgerEntries?: unknown[]; transfers?: unknown[] }> => {
  return request({
    url: `/v1/deposits/${encodeURIComponent(id)}`,
    method: 'GET',
  });
};

export interface LedgerParams {
  offset?: number;
  limit?: number;
}

/** GET /v1/funds/ledger — USDT ledger */
export const getFundsLedger = (
  params: LedgerParams = {}
): Promise<OffsetPage<LedgerRecord>> => {
  return request({
    url: '/v1/funds/ledger',
    method: 'GET',
    data: params,
  });
};

/** POST /v1/exchanges/quote */
export const getExchangeQuote = (payload: {
  direction: ExchangeDirection;
  inputAmount: string;
}): Promise<ExchangeQuote> => {
  return request({
    url: '/v1/exchanges/quote',
    method: 'POST',
    data: payload,
    showError: false,
  });
};

export interface CreateExchangeOptions {
  idempotencyKey?: string;
}

/** POST /v1/exchanges — requires Idempotency-Key */
export const createExchange = (
  quoteId: string,
  options: CreateExchangeOptions = {}
): Promise<ExchangeRecord> => {
  return request({
    url: '/v1/exchanges',
    method: 'POST',
    data: { quoteId },
    // A lost response retried with the same quote must replay, not use a new key.
    idempotencyKey: options.idempotencyKey ?? `exchange-${quoteId}`,
    showError: false,
  });
};

/** GET /v1/exchanges — page size max 20 */
export const listExchanges = (
  offset = 0
): Promise<OffsetPage<ExchangeRecord>> => {
  return request({
    url: '/v1/exchanges',
    method: 'GET',
    data: { offset },
  });
};

/** GET /v1/exchanges/:id — §20.2 成交详情 + 关联账本 */
export const getExchange = (
  id: string
): Promise<{
  record: ExchangeRecord;
  status?: string;
  rateNumerator?: string | number;
  rateDenominator?: string | number;
  ruleVersion?: string | number;
  feeBps?: number;
  quoteExpiresAt?: string;
  usdtDecimals?: number;
  ledgerEntries?: unknown[];
  [key: string]: unknown;
}> => {
  return request({
    url: `/v1/exchanges/${encodeURIComponent(id)}`,
    method: 'GET',
  });
};

/** GET /v1/withdrawals/config */
export const getWithdrawalsConfig = (): Promise<WithdrawalConfig> => {
  return request({
    url: '/v1/withdrawals/config',
    method: 'GET',
  });
};

export interface CreateWithdrawalOptions {
  idempotencyKey?: string;
}

/** POST /v1/withdrawals — requires Idempotency-Key */
export const createWithdrawal = (
  grossAmount: string,
  options: CreateWithdrawalOptions = {}
): Promise<Withdrawal> => {
  return request({
    url: '/v1/withdrawals',
    method: 'POST',
    data: { grossAmount },
    idempotencyKey:
      options.idempotencyKey ?? createIdempotencyKey('withdrawal'),
    showError: false,
  });
};

/** GET /v1/withdrawals */
export const listWithdrawals = (
  offset = 0
): Promise<OffsetPage<Withdrawal>> => {
  return request({
    url: '/v1/withdrawals',
    method: 'GET',
    data: { offset },
  });
};

/** GET /v1/withdrawals/:id — §20.2 */
export const getWithdrawal = (
  id: string
): Promise<{
  record: Withdrawal;
  decimals?: number;
  ledgerEntries?: unknown[];
  [key: string]: unknown;
}> => {
  return request({
    url: `/v1/withdrawals/${encodeURIComponent(id)}`,
    method: 'GET',
  });
};
